import { dateToTimezonedFormated } from 'common/date.ts';
import { getCountryCode, getRuntimeTimezone, isGranted, truncate } from 'common/helpers.ts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Shipment } from 'types/Shipment.ts';
import writeXlsxFile, { type Cell } from 'write-excel-file/browser';
import { useDateFormat } from '#admin/hook/useDateFormat.ts';
import { useEnv } from '#admin/hook/useEnv.tsx';
import { useMe } from '#admin/hook/useMe.tsx';

const formatDatetime = (datetime: string | null | undefined, timezone: string, currentDateFormat: string) => {
  const timezoneToUse = timezone;
  const formattedDatetime = datetime ? dateToTimezonedFormated(datetime, timezoneToUse, undefined, currentDateFormat) : null;
  const formattedTimezone = timezoneToUse.split('/')[1];

  return formattedDatetime ? `${formattedDatetime} (${formattedTimezone})` : '-';
};

export const useXLSXShipments = (shipments: Shipment[]) => {
  const { t } = useTranslation();
  const { currentDateFormat } = useDateFormat();
  const env = useEnv();
  const { me } = useMe();

  const data = React.useMemo(() => {
    if (!shipments.length) return null;

    const maxPackages = Math.max(...shipments.map((s) => s.packages.length), 0);

    const titles = {
      shipment: {
        orderNumber: t('views.shipment.labels.orderNumber'),
        owner: t('views.shipment.labels.owner'),
        customer: t('views.shipment.labels.customer'),
      },
      leg: {
        exId: t('components.leg.labels.logistics_ex_id'),
        supplier: t('components.leg.labels.supplier'),
        driver: t('components.leg.labels.driver'),
        pickupAddress: t('components.leg.labels.pickupAddress'),
        pickupCity: t('labels.city'),
        pickupCountry: t('labels.country'),
        pickupPostcode: t('labels.postalCode'),
        expectedPickupTime: t('views.shipment.labels.expectedPickupTime'),
        actualPickupTime: t('components.leg.labels.actualPickupTime'),
        deliveryAddress: t('components.leg.labels.deliveryAddress'),
        deliveryCity: t('labels.city'),
        deliveryCountry: t('labels.country'),
        deliveryPostcode: t('labels.postalCode'),
        expectedDeliveryTime: t('components.leg.labels.expectedDeliveryTime'),
        actualDeliveryTime: t('components.leg.labels.actualDeliveryTime'),
      },
      package: {
        package: t('views.shipment.labels.package'),
        id: t('labels.id'),
        description: t('views.shipment.labels.description'),
        quantity: t('views.shipment.labels.quantity'),
        temperature: t('views.shipment.sections.packages.columns.temperature'),
        range: t('views.shipment.sections.packages.columns.range'),
      },
    };

    const mapToTitle = (value: string): Cell => ({ value, fontWeight: 'bold' });
    const mapToString = (value: string | null | undefined): Cell => ({ value: value || '', type: String });
    const mapToNumber = (value: number | null | undefined): Cell => ({ value: value ?? '', type: Number });
    const mapEmpty = (): Cell => ({ value: '', type: String });

    const packageHeaders = Array.from({ length: maxPackages }, (_, i) => {
      const n = i + 1;
      const prefix = `${titles.package.package} ${n}`;

      return [
        `${prefix} ${titles.package.id}`,
        `${prefix} ${titles.package.description}`,
        `${prefix} ${titles.package.quantity}`,
        ...(env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID) ? [`${prefix} ${titles.package.temperature}`, `${prefix} ${titles.package.range}`] : []),
      ].map(mapToTitle);
    }).flat();

    const headerRow = [
      titles.shipment.orderNumber,
      titles.shipment.owner,
      titles.shipment.customer,
      titles.leg.exId,
      titles.leg.supplier,
      titles.leg.driver,
      titles.leg.pickupAddress,
      titles.leg.pickupCity,
      titles.leg.pickupCountry,
      titles.leg.pickupPostcode,
      titles.leg.expectedPickupTime,
      titles.leg.actualPickupTime,
      titles.leg.deliveryAddress,
      titles.leg.deliveryCity,
      titles.leg.deliveryCountry,
      titles.leg.deliveryPostcode,
      titles.leg.expectedDeliveryTime,
      titles.leg.actualDeliveryTime,
    ]
      .map(mapToTitle)
      .concat(packageHeaders);

    const dataRows = shipments.flatMap((shipment) =>
      shipment.legs.map((leg) => {
        const packageCells = Array.from({ length: maxPackages }, (_, i) => {
          const pkg = shipment.packages[i];
          if (!pkg) {
            return [mapEmpty(), mapEmpty(), mapEmpty(), mapEmpty(), mapEmpty()];
          }
          return [
            mapToString(pkg.logistics_ex_id),
            mapToString(pkg.description || '-'),
            pkg.quantity !== null ? mapToNumber(pkg.quantity) : mapToString('-'),
            ...(env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID)
              ? [
                  mapToString(pkg.temperature ? pkg.temperature + 'ºC' : '-'),
                  mapToString(pkg.th_max_temperature && pkg.th_min_temperature ? `${pkg.th_min_temperature}ºC / ${pkg.th_max_temperature}ºC` : '-'),
                ]
              : []),
          ];
        }).flat();

        return [
          mapToString(shipment.logistics_ex_id),
          mapToString(shipment.owner.name),
          mapToString(shipment.customer.name),
          mapToString(leg.logistics_ex_id),
          mapToString(leg.supplier ? leg.supplier.name : ''),
          mapToString(leg.driver ? `${leg.driver.name} - ${truncate(leg.driver.id, 8, '').toUpperCase()}` : ''),
          mapToString(leg.origin_address_name),
          mapToString(leg.origin_city),
          mapToString(leg.origin_country ? (getCountryCode(leg.origin_country) ?? leg.origin_country) : ''),
          mapToString(leg.origin_postcode),
          mapToString(formatDatetime(leg.origin_expected_datetime, leg.origin_tz || getRuntimeTimezone(), currentDateFormat)),
          mapToString(formatDatetime(leg.origin_actual_datetime, leg.origin_tz || getRuntimeTimezone(), currentDateFormat)),
          mapToString(leg.destination_address_name),
          mapToString(leg.destination_city),
          mapToString(leg.destination_country ? (getCountryCode(leg.destination_country) ?? leg.destination_country) : ''),
          mapToString(leg.destination_postcode),
          mapToString(formatDatetime(leg.destination_expected_datetime, leg.destination_tz || getRuntimeTimezone(), currentDateFormat)),
          mapToString(formatDatetime(leg.destination_actual_datetime, leg.destination_tz || getRuntimeTimezone(), currentDateFormat)),
          ...packageCells,
        ];
      }),
    );

    return [headerRow, ...dataRows];
  }, [shipments, t, currentDateFormat, env, me]);

  const exportToXLSX = React.useCallback(async () => {
    if (!data) return;

    const fileName = shipments.length === 1 ? `shipment_${shipments[0].logistics_ex_id}.xlsx` : 'shipments_export.xlsx';

    await writeXlsxFile(data, {
      sheet: 'Shipments',
      fileName,
    });
  }, [data, shipments]);

  return { exportToXLSX };
};
